import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import OnboardingWizard from '@/components/OnboardingWizard';
import EmployerOnboarding from '@/components/EmployerOnboarding';
import LoginActivityCard from '@/components/admin/LoginActivityCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Briefcase, Users, FileText, TrendingUp, Plus, 
  ArrowUpRight, Clock, CheckCircle, XCircle, Eye,
  Building2, GraduationCap, Shield, BarChart3, Gauge, UserPlus, ChevronDown
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart as RPieChart, Pie, Cell, Legend
} from 'recharts';

const Dashboard = () => {
  const { user, token } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const isCandidate = user?.role === 'candidate';
  const isEmployer = ['client_admin', 'team_leader', 'recruiter'].includes(user?.role);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showEmployerOnboarding, setShowEmployerOnboarding] = useState(false);

  useEffect(() => {
    if (isCandidate && token) {
      const headers = { Authorization: `Bearer ${token}` };
      axios.get(`${API}/educator/profile`, { headers, withCredentials: true })
        .then(res => {
          const p = res.data;
          if (!p.onboarding_completed && (p.profile_completion || 0) < 50) {
            setShowOnboarding(true);
          }
        })
        .catch(() => {});
    }
    if (isEmployer && token) {
      const headers = { Authorization: `Bearer ${token}` };
      axios.get(`${API}/tenant/onboarding-status`, { headers, withCredentials: true })
        .then(res => {
          if (res.data?.needs_onboarding) {
            setShowEmployerOnboarding(true);
          }
        })
        .catch(() => {});
    }
  }, [isCandidate, isEmployer, token]);

  return (
    <DashboardLayout>
      {showOnboarding && (
        <OnboardingWizard onComplete={() => setShowOnboarding(false)} />
      )}
      {showEmployerOnboarding && (
        <EmployerOnboarding onComplete={() => setShowEmployerOnboarding(false)} />
      )}
      {isSuperAdmin ? (
        // Super admin: single unified admin console (no duplicate dashboard)
        <Navigate to="/admin" replace />
      ) : (
        <StandardDashboard user={user} token={token} />
      )}
    </DashboardLayout>
  );
};

/* ==================== SUPER ADMIN DASHBOARD ==================== */
const SuperAdminDashboard = ({ user, token }) => {
  const [stats, setStats] = useState(null);
  const [candAnalytics, setCandAnalytics] = useState(null);
  const [clientData, setClientData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trendGranularity, setTrendGranularity] = useState('day');
  const [trendPeriods, setTrendPeriods] = useState(30);
  const [trendData, setTrendData] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/dashboard`, { headers, withCredentials: true }),
      axios.get(`${API}/admin/analytics/candidates`, { headers, withCredentials: true }),
      axios.get(`${API}/admin/analytics/clients`, { headers, withCredentials: true }),
    ])
      .then(([dashRes, candRes, clientRes]) => {
        setStats(dashRes.data);
        setCandAnalytics(candRes.data);
        setClientData(clientRes.data.clients || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    setTrendLoading(true);
    axios.get(`${API}/admin/analytics/trends`, {
      headers, withCredentials: true,
      params: { granularity: trendGranularity, periods: trendPeriods },
    })
      .then(res => setTrendData(res.data.data || []))
      .catch(() => setTrendData([]))
      .finally(() => setTrendLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, trendGranularity, trendPeriods]);

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
    </div>
  );

  const s = stats?.stats || {};

  const statCards = [
    { label: 'Candidates', value: s.total_candidates, icon: GraduationCap, color: 'bg-blue-50 text-blue-600', border: 'border-blue-100' },
    { label: 'Employers', value: s.total_employers, icon: Building2, color: 'bg-indigo-50 text-indigo-600', border: 'border-indigo-100', sub: `${s.paid_employers || 0} paid / ${s.free_employers || 0} free` },
    { label: 'Active Jobs', value: s.active_jobs, icon: Briefcase, color: 'bg-green-50 text-green-600', border: 'border-green-100' },
    { label: 'Applications', value: s.total_applications, icon: FileText, color: 'bg-amber-50 text-amber-600', border: 'border-amber-100' },
  ];

  const candPieData = candAnalytics ? [
    { name: 'Applied', value: candAnalytics.applied, fill: '#3B82F6' },
    { name: 'Not Applied', value: candAnalytics.not_applied, fill: '#E2E8F0' },
  ] : [];

  const statusData = candAnalytics?.status_breakdown
    ? Object.entries(candAnalytics.status_breakdown).map(([key, val]) => ({ name: key, value: val }))
    : [];

  const clientChartData = clientData.map(c => ({
    name: c.name?.length > 15 ? c.name.slice(0, 15) + '...' : c.name,
    jobs: c.total_jobs,
    applications: c.total_applications,
    hired: c.hired,
  }));

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="superadmin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <div className="flex items-center gap-2 mt-2">
            <p className="text-slate-600">Platform overview</p>
            <Badge className="bg-red-100 text-red-800">Super Admin</Badge>
          </div>
        </div>
        <Link to="/admin">
          <Button data-testid="go-to-admin-btn">
            <Shield className="w-4 h-4 mr-2" />
            Admin Console
          </Button>
        </Link>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((c) => (
          <Card key={c.label} className={`border ${c.border}`} data-testid={`sa-stat-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.color}`}>
                  <c.icon className="w-5 h-5" />
                </div>
              </div>
              <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{c.value ?? 0}</p>
              <p className="text-sm text-slate-500 mt-1">{c.label}</p>
              {c.sub && <p className="text-xs text-slate-400 mt-0.5">{c.sub}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Trend Charts */}
      <div className="space-y-3" data-testid="trends-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Trends</h2>
            <p className="text-xs text-slate-500">Real signup and application activity over time</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="trend-filters">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="granularity-toggle">
              {['day', 'month', 'year'].map(g => (
                <button
                  key={g}
                  type="button"
                  onClick={() => {
                    setTrendGranularity(g);
                    setTrendPeriods(g === 'day' ? 30 : g === 'month' ? 12 : 5);
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md capitalize transition-colors ${trendGranularity === g ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                  data-testid={`granularity-${g}`}
                >
                  {g}
                </button>
              ))}
            </div>
            <Select value={String(trendPeriods)} onValueChange={(v) => setTrendPeriods(parseInt(v, 10))}>
              <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="trend-periods">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {trendGranularity === 'day' && (
                  <>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="14">Last 14 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="60">Last 60 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                  </>
                )}
                {trendGranularity === 'month' && (
                  <>
                    <SelectItem value="3">Last 3 months</SelectItem>
                    <SelectItem value="6">Last 6 months</SelectItem>
                    <SelectItem value="12">Last 12 months</SelectItem>
                    <SelectItem value="24">Last 24 months</SelectItem>
                  </>
                )}
                {trendGranularity === 'year' && (
                  <>
                    <SelectItem value="3">Last 3 years</SelectItem>
                    <SelectItem value="5">Last 5 years</SelectItem>
                    <SelectItem value="10">Last 10 years</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card data-testid="candidates-trend-card">
            <CardHeader className="pb-2"><CardTitle style={{ fontFamily: 'Outfit' }}>Candidates Trend</CardTitle></CardHeader>
            <CardContent>
              <div className="h-[250px]">
                {trendLoading ? (
                  <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" /></div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <defs>
                        <linearGradient id="candGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="candidates" name="Candidates" stroke="#3B82F6" fill="url(#candGrad)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="applications-trend-card">
            <CardHeader className="pb-2"><CardTitle style={{ fontFamily: 'Outfit' }}>Job Applications Trend</CardTitle></CardHeader>
            <CardContent>
              <div className="h-[250px]">
                {trendLoading ? (
                  <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" /></div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <defs>
                        <linearGradient id="appGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#10B981" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="applications" name="Applications" stroke="#10B981" fill="url(#appGrad)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Candidate Analytics + Client Performance */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Candidate Activity</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <RPieChart>
                  <Pie data={candPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value">
                    {candPieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </RPieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-2 text-sm">
              <div className="text-center">
                <p className="text-lg font-bold text-blue-600">{candAnalytics?.applied || 0}</p>
                <p className="text-slate-500">Applied</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-slate-400">{candAnalytics?.not_applied || 0}</p>
                <p className="text-slate-500">Not Applied</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Application Status</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statusData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#6366F1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <LoginActivityCard token={token} defaultLimit={6} />
      </div>

      {/* Recent Signups */}
      <Card data-testid="recent-signups-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                <UserPlus className="w-4 h-4 text-emerald-600" />
              </div>
              <CardTitle style={{ fontFamily: 'Outfit' }}>Recent Signups</CardTitle>
            </div>
            <Link to="/admin"><Button variant="ghost" size="sm" data-testid="signups-view-all">View All <ArrowUpRight className="w-3 h-3 ml-1" /></Button></Link>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {(stats?.recent_users || []).length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No recent signups</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {stats?.recent_users?.slice(0, 9).map((u) => (
                <div key={u.user_id} className="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-colors" data-testid={`signup-${u.user_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 text-sm truncate">{u.name || '—'}</p>
                    <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    {u.created_at && (
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        Joined {new Date(u.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                  <RoleBadge role={u.role} />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Performance */}
      {clientChartData.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle style={{ fontFamily: 'Outfit' }}>Client Performance</CardTitle>
              <Link to="/admin"><Button variant="ghost" size="sm">Detailed Analytics <ArrowUpRight className="w-3 h-3 ml-1" /></Button></Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={clientChartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="jobs" fill="#6366F1" name="Job Posts" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="applications" fill="#3B82F6" name="Applications" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="hired" fill="#10B981" name="Hired" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Client Table */}
      {clientData.length > 0 && (
        <Card>
          <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Employer Summary</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Institution</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Tier</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Total Jobs</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Active Jobs</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Applications</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Est. Views</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Hired</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Rejected</th>
                  </tr>
                </thead>
                <tbody>
                  {clientData.map((c) => (
                    <tr key={c.org_id} className="border-b last:border-0 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{c.name}</p>
                        <p className="text-xs text-slate-500">{c.institution_type || '—'}</p>
                      </td>
                      <td className="px-4 py-3 text-center"><TierBadge tier={c.subscription_tier} /></td>
                      <td className="px-4 py-3 text-center font-medium">{c.total_jobs}</td>
                      <td className="px-4 py-3 text-center">{c.active_jobs}</td>
                      <td className="px-4 py-3 text-center font-medium text-blue-600">{c.total_applications}</td>
                      <td className="px-4 py-3 text-center text-slate-500">{c.views}</td>
                      <td className="px-4 py-3 text-center text-green-600 font-medium">{c.hired}</td>
                      <td className="px-4 py-3 text-center text-red-500">{c.rejected}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card data-testid="sa-quick-actions">
        <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Quick Actions</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/admin" className="block">
              <div className="p-4 bg-slate-50 rounded-xl text-center hover:bg-slate-100 transition-colors">
                <Shield className="w-6 h-6 text-red-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">Admin Console</p>
              </div>
            </Link>
            <Link to="/jobs" className="block">
              <div className="p-4 bg-slate-50 rounded-xl text-center hover:bg-slate-100 transition-colors">
                <Eye className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">Browse Jobs</p>
              </div>
            </Link>
            <Link to="/jobs/create" className="block">
              <div className="p-4 bg-slate-50 rounded-xl text-center hover:bg-slate-100 transition-colors">
                <Plus className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">Post Job</p>
              </div>
            </Link>
            <Link to="/team" className="block">
              <div className="p-4 bg-slate-50 rounded-xl text-center hover:bg-slate-100 transition-colors">
                <Users className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">Team</p>
              </div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ==================== STANDARD DASHBOARD (non super_admin) ==================== */
const StandardDashboard = ({ user, token }) => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [tenantLimits, setTenantLimits] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const promises = [
          axios.get(`${API}/analytics/dashboard`, { headers, withCredentials: true }),
          axios.get(`${API}/analytics/recruitment-funnel`, { headers, withCredentials: true })
        ];
        // Fetch tenant limits for admins/recruiters
        if (['client_admin', 'team_leader', 'recruiter'].includes(user?.role)) {
          promises.push(axios.get(`${API}/tenant/limits`, { headers, withCredentials: true }));
        }
        const results = await Promise.all(promises);
        setAnalytics(results[0].data);
        setFunnel(results[1].data.funnel);
        if (results[2]) setTenantLimits(results[2].data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, user?.role]);

  const isRecruiter = ['client_admin', 'team_leader', 'recruiter'].includes(user?.role);
  const isCandidate = user?.role === 'candidate';

  const funnelData = funnel ? [
    { name: 'Applied', value: funnel.applied || 0, fill: '#3B82F6' },
    { name: 'Screening', value: funnel.screening || 0, fill: '#F59E0B' },
    { name: 'Interview', value: funnel.interview || 0, fill: '#8B5CF6' },
    { name: 'Offer', value: funnel.offer || 0, fill: '#10B981' },
    { name: 'Hired', value: funnel.hired || 0, fill: '#059669' },
  ] : [];

  const getRoleBadge = (role) => {
    const roleConfig = {
      client_admin: { label: 'Admin', className: 'bg-purple-100 text-purple-800' },
      team_leader: { label: 'Team Lead', className: 'bg-blue-100 text-blue-800' },
      recruiter: { label: 'Recruiter', className: 'bg-green-100 text-green-800' },
      candidate: { label: 'Candidate', className: 'bg-slate-100 text-slate-800' },
    };
    const config = roleConfig[role] || roleConfig.candidate;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'hired': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'interview': return <Clock className="w-4 h-4 text-purple-600" />;
      default: return <Clock className="w-4 h-4 text-blue-600" />;
    }
  };

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="dashboard-container">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <div className="flex items-center gap-2 mt-2">
            <p className="text-slate-600">Your dashboard overview</p>
            {getRoleBadge(user?.role)}
          </div>
        </div>
        {isRecruiter && (
          <Link to="/jobs/create">
            <Button data-testid="create-job-btn"><Plus className="w-4 h-4 mr-2" />Post New Job</Button>
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <Card className="card-hover" data-testid="stat-total-jobs">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">{isCandidate ? 'Jobs Applied' : 'Total Jobs'}</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{loading ? '-' : (isCandidate ? (analytics?.total_applications || 0) : (analytics?.total_jobs || 0))}</p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover" data-testid="stat-active-jobs">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Active Jobs</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{loading ? '-' : analytics?.active_jobs || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover" data-testid="stat-applications">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">{isCandidate ? 'My Applications' : 'Applications'}</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{loading ? '-' : analytics?.total_applications || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover" data-testid="stat-hired">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">{isCandidate ? 'Interviews' : 'Hired'}</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{loading ? '-' : (analytics?.status_breakdown?.hired || analytics?.status_breakdown?.interview || 0)}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tenant Quota Usage */}
      {isRecruiter && tenantLimits && tenantLimits.tier !== 'none' && (
        <Card data-testid="quota-usage-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit' }}>
                <Gauge className="w-4 h-4 text-indigo-600" />
                Plan Usage
                <Badge className="ml-1 text-xs bg-indigo-100 text-indigo-700 capitalize">{tenantLimits.tier}</Badge>
              </CardTitle>
              <Link to="/pricing"><Button variant="ghost" size="sm">Upgrade <ArrowUpRight className="w-3 h-3 ml-1" /></Button></Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-600">Job Postings</span>
                  <span className="font-medium text-slate-900">
                    {tenantLimits.jobs.unlimited ? `${tenantLimits.jobs.current} (Unlimited)` : `${tenantLimits.jobs.current} / ${tenantLimits.jobs.limit}`}
                  </span>
                </div>
                {!tenantLimits.jobs.unlimited && (
                  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        tenantLimits.jobs.limit > 0 && tenantLimits.jobs.current / tenantLimits.jobs.limit > 0.8
                          ? 'bg-amber-500' : 'bg-indigo-500'
                      }`}
                      style={{ width: `${tenantLimits.jobs.limit > 0 ? Math.min(100, (tenantLimits.jobs.current / tenantLimits.jobs.limit) * 100) : 0}%` }}
                    />
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-600">Team Members</span>
                  <span className="font-medium text-slate-900">
                    {tenantLimits.users.unlimited ? `${tenantLimits.users.current} (Unlimited)` : `${tenantLimits.users.current} / ${tenantLimits.users.limit}`}
                  </span>
                </div>
                {!tenantLimits.users.unlimited && (
                  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        tenantLimits.users.limit > 0 && tenantLimits.users.current / tenantLimits.users.limit > 0.8
                          ? 'bg-amber-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${tenantLimits.users.limit > 0 ? Math.min(100, (tenantLimits.users.current / tenantLimits.users.limit) * 100) : 0}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
            {tenantLimits.listings_remaining != null && (
              <p className="text-xs text-slate-500 mt-2">Pack listings remaining: <span className="font-semibold">{tenantLimits.listings_remaining}</span></p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {isRecruiter && funnelData.length > 0 && (
          <Card className="lg:col-span-2" data-testid="recruitment-funnel">
            <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Recruitment Funnel</CardTitle></CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={funnelData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={80} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className={isRecruiter ? '' : 'lg:col-span-3'} data-testid="recent-applications">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle style={{ fontFamily: 'Outfit' }}>Recent Applications</CardTitle>
            <Link to="/applications"><Button variant="ghost" size="sm">View All <ArrowUpRight className="w-4 h-4 ml-1" /></Button></Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-8 text-slate-500">Loading...</div>
              ) : analytics?.recent_applications?.length > 0 ? (
                analytics.recent_applications.map((app) => (
                  <div
                    key={app.app_id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                    onClick={() => navigate(`/applications/${app.app_id}`)}
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(app.status)}
                      <div>
                        <p className="font-medium text-slate-900">{app.job?.title || 'Unknown Job'}</p>
                        <p className="text-sm text-slate-500">{isRecruiter && app.candidate ? app.candidate.name : ''}</p>
                      </div>
                    </div>
                    <Badge className={`status-${app.status}`}>{app.status}</Badge>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No applications yet</p>
                  {isCandidate && <Link to="/jobs"><Button variant="outline" className="mt-3">Browse Jobs</Button></Link>}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="quick-actions">
        <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Quick Actions</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/jobs" className="block">
              <div className="p-4 bg-slate-50 rounded-xl text-center hover:bg-slate-100 transition-colors">
                <Eye className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">Browse Jobs</p>
              </div>
            </Link>
            <Link to="/applications" className="block">
              <div className="p-4 bg-slate-50 rounded-xl text-center hover:bg-slate-100 transition-colors">
                <FileText className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">Applications</p>
              </div>
            </Link>
            {isCandidate && (
              <Link to="/profile" className="block">
                <div className="p-4 bg-slate-50 rounded-xl text-center hover:bg-slate-100 transition-colors">
                  <Users className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-700">My Profile</p>
                </div>
              </Link>
            )}
            {isRecruiter && (
              <>
                <Link to="/jobs/create" className="block">
                  <div className="p-4 bg-slate-50 rounded-xl text-center hover:bg-slate-100 transition-colors">
                    <Plus className="w-6 h-6 text-green-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-700">Post Job</p>
                  </div>
                </Link>
                <Link to="/team" className="block">
                  <div className="p-4 bg-slate-50 rounded-xl text-center hover:bg-slate-100 transition-colors">
                    <Users className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-700">Team</p>
                  </div>
                </Link>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ==================== SHARED ==================== */
const RoleBadge = ({ role }) => {
  const map = { super_admin: 'bg-red-100 text-red-700', client_admin: 'bg-purple-100 text-purple-700', team_leader: 'bg-blue-100 text-blue-700', recruiter: 'bg-green-100 text-green-700', candidate: 'bg-slate-100 text-slate-700' };
  const labels = { super_admin: 'Super Admin', client_admin: 'Client Admin', team_leader: 'Team Lead', recruiter: 'Recruiter', candidate: 'Candidate' };
  return <Badge className={map[role] || map.candidate}>{labels[role] || role}</Badge>;
};

const TierBadge = ({ tier }) => {
  const map = { basic: 'bg-slate-100 text-slate-700 border-slate-200', pro: 'bg-blue-100 text-blue-700 border-blue-200', enterprise: 'bg-purple-100 text-purple-700 border-purple-200' };
  return <Badge className={`border ${map[tier] || map.basic}`}>{tier || 'basic'}</Badge>;
};

export default Dashboard;
